/**
 * Typed fetch wrapper with auth + error handling.
 *
 * Uses credentials: 'same-origin' (cookies).
 * Auto-retries on 401 (refresh token, retry once).
 * Redirects to login on auth failure.
 */

import { refresh, login } from '../auth';
import { trace } from './debug-stream';

// Build-SHA drift detection — REST counterpart to the WS `system.build_info`
// hello frame. Server stamps every response with `X-Build-Sha`. The first
// SHA we observe is pinned for the session; later responses with a
// different SHA mean a rolling deploy happened while this tab was open.
// Fires the same `apex:version-drift` event the WS path uses so the toast
// appears once regardless of which channel detects it first. "dev" SHAs
// (local builds without --build-arg) are treated as no-op.
let firstApiBuildSha: string | null = null;
let apiDriftDispatched = false;
const API_VERSION_DRIFT_EVENT = 'apex:version-drift';

function checkBuildSha(res: Response): void {
  const sha = res.headers.get('X-Build-Sha');
  if (!sha) return;
  if (firstApiBuildSha === null) {
    firstApiBuildSha = sha;
    return;
  }
  if (
    !apiDriftDispatched &&
    sha !== firstApiBuildSha &&
    sha !== 'dev' &&
    firstApiBuildSha !== 'dev'
  ) {
    apiDriftDispatched = true;
    window.dispatchEvent(
      new CustomEvent(API_VERSION_DRIFT_EVENT, {
        detail: { from: firstApiBuildSha, to: sha },
      }),
    );
  }
}

export async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await apiFetch(path, opts);
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = params ? `${path}?${new URLSearchParams(params)}` : path;
  return api<T>(url);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete<T>(path: string): Promise<T> {
  return api<T>(path, {
    method: 'DELETE',
  });
}

/**
 * Raw fetch with 401 auto-retry and timeout. Returns the Response object.
 * Use this when you need access to response status/headers directly.
 *
 * Includes a 30-second timeout to prevent indefinite hangs when the
 * server or auth middleware is unresponsive.
 */
export async function apiFetch(url: string, opts?: RequestInit): Promise<Response> {
  const method = opts?.method || 'GET';
  const t = trace('api', `${method} ${url}`);
  const start = Date.now();

  const options: RequestInit = { ...opts, credentials: 'same-origin' };

  // Add timeout via AbortController to prevent infinite hangs
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  const fetchOpts: RequestInit = { ...options, signal: controller.signal };

  let res: Response;
  try {
    res = await fetch(url, fetchOpts);
  } catch (err) {
    clearTimeout(timeoutId);
    const error =
      (err as Error).name === 'AbortError' ? new Error('Request timed out') : (err as Error);
    t.fail(error);
    throw error;
  }
  clearTimeout(timeoutId);
  checkBuildSha(res);

  if (res.status !== 401) {
    t.ok({ status: res.status, ms: Date.now() - start });
    return res;
  }

  // Try refresh (with its own timeout)
  const ok = await refresh();
  if (!ok) {
    login();
    t.ok({ status: 401, ms: Date.now() - start, refreshed: false });
    return res;
  }

  // Retry the original request
  const retryController = new AbortController();
  const retryTimeoutId = setTimeout(() => retryController.abort(), 30000);
  try {
    const retryRes = await fetch(url, { ...options, signal: retryController.signal });
    clearTimeout(retryTimeoutId);
    checkBuildSha(retryRes);
    t.ok({ status: retryRes.status, ms: Date.now() - start, retried: true });
    return retryRes;
  } catch (err) {
    clearTimeout(retryTimeoutId);
    const error =
      (err as Error).name === 'AbortError'
        ? new Error('Request timed out after auth retry')
        : (err as Error);
    t.fail(error);
    throw error;
  }
}
